import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";
import { CustomAttribute } from "@/types";

// Custom field interface - matches the one from useCustomFields
interface CustomField {
  field_id: number;
  field_name: string;
  editor_type: "textBox" | "dropDown" | "dateBox" | "tagBox";
  description?: string;
  options?: string | string[];
  is_required?: number;
  enabled: number;
  type_ids?: number[];
  sort_value: number;
}

// Interface for objects that can have custom attributes
interface WithCustomAttributes {
  custom_attributes?: CustomAttribute[];
}

// Type for API payload custom attributes
export interface ApiCustomAttribute {
  field_id: number;
  field_name: string;
  item_id: number;
  value: string | number | string[] | null;
}

/**
 * Process custom attributes from form data into API format for contacts
 */
export const processCustomAttributesForAPI = (
  formData: Record<string, any>,
  customFields: CustomField[],
  contactId: number
): ApiCustomAttribute[] => {
  const customAttributes: ApiCustomAttribute[] = [];

  for (const key in formData) {
    if (key.indexOf("custom_attribute_") !== -1) {
      const field_id = parseInt(key.replace("custom_attribute_", ""));

      // Find the base attribute definition to get field_name
      const baseAttr = customFields?.find((attr) => attr.field_id === field_id);

      customAttributes.push({
        field_id: field_id,
        field_name: baseAttr?.field_name || `Field ${field_id}`,
        item_id: contactId,
        value: formData[key] as string | number | string[] | null,
      });
    }
  }

  return customAttributes;
};

/**
 * Process custom attributes for editing - only returns changed attributes
 */
export const processEditCustomAttributesForAPI = (
  formData: Record<string, any>,
  customFields: CustomField[],
  contactId: number,
  existingCustomAttributes: CustomAttribute[] = []
): ApiCustomAttribute[] => {
  const customAttributes: ApiCustomAttribute[] = [];

  for (const key in formData) {
    if (key.indexOf("custom_attribute_") !== -1) {
      const field_id = parseInt(key.replace("custom_attribute_", ""));
      const currentAttr = existingCustomAttributes?.find(
        (attr) => attr.field_id === field_id
      );

      // Compare values properly, especially for TagBox arrays
      let valuesAreEqual = false;
      if (currentAttr) {
        const currentValue = safeParseJSON(currentAttr.value);
        const newValue = safeParseJSON(String(formData[key] || ""));
        if (Array.isArray(currentValue) && Array.isArray(newValue)) {
          valuesAreEqual = arraysEqual(currentValue, newValue);
        } else {
          valuesAreEqual = currentValue === newValue;
        }
      }

      // Skip if the value hasn't actually changed
      if (valuesAreEqual) {
        continue;
      }

      // Find the base attribute definition to get field_name
      const baseAttr = customFields?.find((attr) => attr.field_id === field_id);

      customAttributes.push({
        field_id: field_id,
        field_name:
          baseAttr?.field_name ||
          currentAttr?.field_name ||
          `Field ${field_id}`,
        item_id: contactId,
        value: formData[key] as string | number | string[] | null,
      });
    }
  }

  return customAttributes;
};

/**
 * Transform custom_attributes array into individual form field properties
 */
export const transformCustomAttributes = (
  data: WithCustomAttributes
): Record<string, any> => {
  if (!data?.custom_attributes) return {};

  const customFieldValues: Record<string, string | number | string[]> = {};
  data.custom_attributes.forEach((attr: CustomAttribute) => {
    // Handle TagBox fields that store values as JSON strings
    if (attr.editor_type === "tagBox") {
      customFieldValues[`custom_attribute_${attr.field_id}`] =
        typeof attr.value === "object"
          ? attr.value
          : safeParseJSON(attr.value || "[]");
    } else {
      customFieldValues[`custom_attribute_${attr.field_id}`] = attr.value;
    }
  });

  return customFieldValues;
};

/**
 * Helper function for array comparison
 */
export const arraysEqual = (arr1: unknown[], arr2: unknown[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((val, index) => val === arr2[index]);
};

/**
 * Process custom attributes for creation - only includes field_id and value
 * Following the same pattern as case creation
 */
export const processCustomAttributesForCreation = (
  formData: Record<string, any>
): { field_id: number; value: string | number | string[] | null }[] => {
  const customAttributes: {
    field_id: number;
    value: string | number | string[] | null;
  }[] = [];

  for (const key in formData) {
    if (key.indexOf("custom_attribute_") !== -1) {
      const field_id = parseInt(key.replace("custom_attribute_", ""));
      customAttributes.push({
        field_id: field_id,
        value: formData[key] as string | number | string[] | null,
      });
    }
  }

  return customAttributes;
};

/**
 * Extract and remove custom attribute fields from form data
 */
export const extractCustomFieldsFromFormData = (
  formData: Record<string, any>
): Record<string, any> => {
  const cleanedFormData = { ...formData };

  // Remove custom attribute fields from the main form data
  for (const key in cleanedFormData) {
    if (key.indexOf("custom_attribute_") !== -1) {
      delete cleanedFormData[key];
    }
  }

  return cleanedFormData;
};
